import React, { useState, useEffect } from 'react';
import {
    Container,
    Box,
    Typography,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    People as PeopleIcon,
    PostAdd as PostAddIcon,
    Favorite as FavoriteIcon,
    Comment as CommentIcon,
    ShoppingBag as ShoppingBagIcon
} from '@mui/icons-material';
import adminService from '../services/adminService';
import '../styles/ProtectedRoutes.css';

const AdminOverview = () => {
    const [stats, setStats] = useState({
        totalCustomers: 0,
        totalPosts: 0,
        totalLikes: 0,
        totalComments: 0,
        totalProducts: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadDashboardStats();
    }, []);

    const loadDashboardStats = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await adminService.getDashboardStats();

            if (response.success) {
                setStats(response.data);
            } else {
                throw new Error(response.message || 'Failed to load dashboard statistics');
            }
        } catch (error) {
            console.error('Error loading dashboard stats:', error);
            setError(error.message || 'Failed to load dashboard statistics');
            setStats({
                totalCustomers: 0,
                totalPosts: 0,
                totalLikes: 0,
                totalComments: 0,
                totalProducts: 0
            });
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ title, value, icon, color, subtitle }) => (
        <Box
            className="glass-content-container"
            sx={{
                height: '100%',
                minHeight: '132px',
                borderRadius: '12px',
                borderLeft: `3px solid ${color}`,
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 20px -8px ${color}66`,
                },
            }}
        >
            {/* Top row: label + icon */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography
                    variant="caption"
                    className="glass-text-secondary"
                    sx={{
                        fontSize: { xs: '0.7rem', sm: '0.72rem', md: '0.75rem' },
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        lineHeight: 1.3,
                        pr: 1,
                    }}
                >
                    {title}
                </Typography>

                <Box
                    sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '8px',
                        backgroundColor: `${color}1F`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    {React.cloneElement(icon, { sx: { fontSize: 18, color } })}
                </Box>
            </Box>

            {/* Value */}
            <Typography
                variant="h4"
                className="glass-text-primary"
                fontWeight="700"
                sx={{
                    fontSize: { xs: '1.6rem', sm: '1.75rem', md: '1.9rem' },
                    lineHeight: 1.1,
                    letterSpacing: '-0.01em',
                }}
            >
                {loading ? <CircularProgress size={22} className="glass-loading" /> : value}
            </Typography>

            {/* Subtitle */}
            {subtitle && (
                <Typography
                    variant="body2"
                    className="glass-text-secondary"
                    sx={{
                        mt: 0.5,
                        fontSize: { xs: '0.72rem', sm: '0.76rem', md: '0.8rem' },
                        opacity: 0.75,
                    }}
                >
                    {subtitle}
                </Typography>
            )}
        </Box>
    );

    const cards = [
        {
            title: 'Total Customers',
            value: stats.totalCustomers.toLocaleString(),
            icon: <PeopleIcon />,
            color: '#bc965e',
            subtitle: 'Registered users',
        },
        {
            title: 'Total Posts',
            value: stats.totalPosts.toLocaleString(),
            icon: <PostAddIcon />,
            color: '#4CAF50',
            subtitle: 'Published content',
        },
        {
            title: 'Total Likes',
            value: stats.totalLikes.toLocaleString(),
            icon: <FavoriteIcon />,
            color: '#FF6B6B',
            subtitle: 'Across all posts',
        },
        {
            title: 'Total Comments',
            value: stats.totalComments.toLocaleString(),
            icon: <CommentIcon />,
            color: '#2196F3',
            subtitle: 'User engagement',
        },
        {
            title: 'Total Products',
            value: stats.totalProducts.toLocaleString(),
            icon: <ShoppingBagIcon />,
            color: '#9C27B0',
            subtitle: 'In catalog',
        },
    ];

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }} className="glass-content-container">
                <Typography variant="h4" className="glass-title" fontWeight="700" gutterBottom>
                    Admin Dashboard
                </Typography>
                <Typography variant="body1" className="glass-text-secondary">
                    Overview of your fashion studio's performance and statistics
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} className="glass-content-container">
                    {error}
                </Alert>
            )}

            {/* Statistics Cards */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2.5,
                    alignItems: 'stretch',
                }}
            >
                {cards.map((card) => (
                    <Box
                        key={card.title}
                        sx={{
                            flex: {
                                xs: '1 1 100%',
                                sm: '1 1 calc(50% - 10px)',
                                md: '1 1 calc(33.333% - 14px)',
                            },
                            maxWidth: {
                                xs: '100%',
                                sm: 'calc(50% - 10px)',
                                md: 'calc(33.333% - 14px)',
                            },
                        }}
                    >
                        <StatCard {...card} />
                    </Box>
                ))}
            </Box>
        </Container>
    );
};

export default AdminOverview;